import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';

import { useAuth } from '@/modules/auth/hooks/use-auth';
import { enhancedToast } from '@/shared/utils/enhanced-toast';

import {
  getMyReferralCode,
  getMyReferrals,
  getReferralProgram,
  getStoreWallet,
  redeemReferralCode,
} from './api';
import { clearPendingReferral, getPendingReferral, savePendingReferral } from './utils';

/** Query param do link de indicação: `/{slug}?ref=CODIGO`. */
export const REFERRAL_QUERY_PARAM = 'ref';

export const storeReferralKeys = {
  program: (slug: string) => ['store-referral', slug, 'program'] as const,
  me: (slug: string) => ['store-referral', slug, 'me'] as const,
  wallet: (slug: string) => ['store-referral', slug, 'wallet'] as const,
  myReferrals: (slug: string) => ['store-referral', slug, 'my-referrals'] as const,
};

/**
 * Entrada pelo link de indicação (PRAT-12): guarda o `?ref=` como pendente da
 * loja, limpa a URL e, assim que o cliente estiver logado, vincula a indicação
 * na API. Idempotente: a API devolve a mesma indicação se já existir.
 */
export function useStoreReferralCapture(slug: string) {
  const [location, navigate] = useLocation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const capturedRef = useRef(false);
  const redeemingRef = useRef(false);

  useEffect(() => {
    if (capturedRef.current || !slug) return;

    let params: URLSearchParams;
    try {
      params = new URLSearchParams(window.location.search);
    } catch {
      return;
    }
    const raw = params.get(REFERRAL_QUERY_PARAM);
    if (!raw) return;

    capturedRef.current = true;
    const code = savePendingReferral(slug, raw);

    params.delete(REFERRAL_QUERY_PARAM);
    const query = params.toString();
    navigate(`${location}${query ? `?${query}` : ''}`, { replace: true });

    if (code) {
      enhancedToast.success('Você foi indicado!', {
        description: isAuthenticated
          ? 'Faça seu primeiro pedido e ganhe crédito para as próximas compras.'
          : 'Entre na sua conta e faça o primeiro pedido para ganhar crédito.',
      });
    }
  }, [slug, location, navigate, isAuthenticated]);

  useEffect(() => {
    if (!slug || !isAuthenticated || redeemingRef.current) return;
    const code = getPendingReferral(slug);
    if (!code) return;

    redeemingRef.current = true;
    redeemReferralCode(slug, code)
      .then(result => {
        clearPendingReferral(slug);
        if ('message' in result) {
          enhancedToast.info('Indicação não aplicada', { description: result.message });
          return;
        }
        queryClient.invalidateQueries({ queryKey: ['store-referral', slug] });
      })
      .catch(() => {
        // Falha de rede: mantém o código pendente para tentar de novo depois.
      })
      .finally(() => {
        redeemingRef.current = false;
      });
  }, [slug, isAuthenticated, queryClient]);
}

export function useReferralProgram(slug: string) {
  return useQuery({
    queryKey: storeReferralKeys.program(slug),
    queryFn: () => getReferralProgram(slug),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyReferralCode(slug: string, enabled = true) {
  return useQuery({
    queryKey: storeReferralKeys.me(slug),
    queryFn: () => getMyReferralCode(slug),
    enabled: enabled && !!slug,
  });
}

export function useStoreWallet(slug: string, enabled = true) {
  return useQuery({
    queryKey: storeReferralKeys.wallet(slug),
    queryFn: () => getStoreWallet(slug),
    enabled: enabled && !!slug,
  });
}

export function useMyReferrals(slug: string) {
  return useQuery({
    queryKey: storeReferralKeys.myReferrals(slug),
    queryFn: () => getMyReferrals(slug),
    enabled: !!slug,
  });
}
